import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import {
  Flex,
  Button,
  Modal,
  Box,
  Text,
  Divider,
  Icon,
  Loader,
  Dropdown,
  Input
} from "samba-ui";
import styled from "styled-components";
import Creatable from "react-select/creatable";
import Toggle from "react-toggle";
import "react-toggle/style.css";

import { getAcrOutageCountries } from "../api";
import { validateEmail } from "../utils/validators";

const StyledReactTooltip = styled(ReactTooltip)`
  width: 200px;
`;
const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const incidentStateOptions = [
  { label: "START", value: "START" },
  { label: "CONTINUE", value: "CONTINUE" },
  { label: "END", value: "END" },
  { label: "ALL", value: "ALL" }
];

const defaultCountry = { label: "ALL", value: "ALL" };
const defaultIncidentState = { label: "ALL", value: "ALL" };
const StyledInput = styled(Input)`
  width: 300px;
`;
const channelIdError = "Invalid Channel Id";
const emailError = "Email list has invalid email address(es)";

const AddNewAcrQueryModal = ({
  isOpen,
  onClose,
  selectedAcr,
  acrQueries,
  addNewAcrQuery,
  updateAcrQuery
}) => {
  const [countryOptions, setCountyOptions] = useState([defaultCountry]);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const req = async () => {
      const result = await getAcrOutageCountries();
      let { data } = await result.json();
      data = data.map(d => ({ label: d, value: d }));
      data.push(defaultCountry);
      setCountyOptions(data);
    };
    req();
  }, []);

  const [name, setName] = useState("");
  const [callsign, setCallsign] = useState("");
  const [channelId, setChannelId] = useState("");
  const [provider, setProvider] = useState("");
  const [country, setCountry] = useState(defaultCountry);
  const [incidentState, setIncidentState] = useState(defaultIncidentState);
  const [active, setActive] = useState(true);
  const [error, setError] = useState("");

  const [emailList, setEmailList] = useState([]);
  const [filterByTopChannels, setFilterByTopChannels] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedAcr && isOpen) {
      setName(selectedAcr.name);
      setCallsign(selectedAcr["query"].callsign);
      setChannelId(selectedAcr["query"].channel_id);
      setProvider(selectedAcr["query"].provider);
      setCountry({
        label: selectedAcr["query"].country,
        value: selectedAcr["query"].country
      });
      setIncidentState({
        label: selectedAcr["query"].incident_state,
        value: selectedAcr["query"].incident_state
      });
      setActive(selectedAcr.active);
      setEmailList(selectedAcr.emailList.map(e => ({ label: e, value: e })));
      setDuration(selectedAcr["query"].duration);
      setFilterByTopChannels(selectedAcr["query"].top_channels);
    }
  }, [isOpen, selectedAcr]);

  const onRequestClose = () => {
    setName("");
    setCallsign("");
    setChannelId("");
    setProvider("");
    setCountry(defaultCountry);
    setActive(true);
    setError("");
    setEmailList([]);
    setLoading(false);
    setIncidentState(defaultIncidentState);
    setFilterByTopChannels(false);
    onClose();
  };

  const onAddNewClick = async () => {
    setLoading(true);
    let body = {
      name,
      emailList: emailList.map(e => e.value),
      query: {},
      active
    };

    if (callsign) {
      body["query"]["callsign"] = callsign;
    }

    if (provider) {
      body["query"]["provider"] = provider;
    }

    if (country && country.value) {
      body["query"]["country"] = country.value;
    }

    if (incidentState && incidentState.value) {
      body["query"]["incident_state"] = incidentState.value;
    }

    body["query"]["duration"] = duration;

    body["query"]["top_channels"] = filterByTopChannels;

    if (channelId) {
      if (isNaN(channelId)) {
        setError(channelIdError);
        return;
      }
      body["query"]["channel_id"] = channelId;
    }
    if (selectedAcr && selectedAcr.id) {
      await updateAcrQuery(selectedAcr.id, body);
    } else {
      await addNewAcrQuery(body);
    }
    onRequestClose();
  };

  const disabled =
    error ||
    !name ||
    !(callsign || provider || country || channelId) ||
    !emailList ||
    !emailList.length;

  const onNameChange = e => {
    const { value } = e.target;
    setName(value);

    let error = "";

    if (acrQueries[value]) {
      error = "Name must be unique";
    }

    setError(error);
  };

  const onCallsignChange = e => {
    const { value } = e.target;
    setCallsign(value);
  };

  const onProviderChange = e => {
    const { value } = e.target;
    setProvider(value);
  };

  const onChannelIdChange = e => {
    const { value } = e.target;
    // should be numeric
    let error = "";
    if (isNaN(parseInt(value))) {
      error = channelIdError;
    }
    setError(error);
    setChannelId(value);
  };

  const onEmailListChange = selected => {
    if (!selected) {
      return setEmailList([]);
    }

    let error = "";

    const filteredResults = selected.filter(s => validateEmail(s.value));
    if (filteredResults.length !== selected.length) {
      error = emailError;
    }
    setError(error);
    setEmailList(selected);
  };

  const onToggleChange = e => {
    const { checked } = e.target;
    setActive(checked);
  };

  const onFilterByTopChannelsChange = e => {
    setFilterByTopChannels(e.target.checked);
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} width={800}>
      <Modal.Header borderColor="#d6d6d6 !important">
        <Text>
          {selectedAcr && selectedAcr.id
            ? "Update the Query"
            : "Add a New Query"}
        </Text>
      </Modal.Header>
      <Modal.Content minHeight={350}>
        {loading ? (
          <Flex alignItems="center" justifyContent="center">
            <Loader />
          </Flex>
        ) : (
          <Box>
            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Name:</Text>
              <StyledInput onChange={onNameChange} value={name} />
            </Flex>

            <Divider />

            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Country:</Text>
              <Box width="300px">
                <Dropdown
                  options={countryOptions}
                  selected={country}
                  onSelect={setCountry}
                />
              </Box>
            </Flex>
            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Incident State:</Text>
              <Box width="300px">
                <Dropdown
                  options={incidentStateOptions}
                  selected={incidentState}
                  onSelect={setIncidentState}
                />
              </Box>
            </Flex>

            {country && country.value === "ALL" ? (
              <Flex alignItems="center" justifyContent="space-between" mb={2}>
                <Text>Channel Id:</Text>
                <StyledInput onChange={onChannelIdChange} value={channelId} />
              </Flex>
            ) : (
              <Flex alignItems="center" justifyContent="space-between" mb={2}>
                <Text>Filter By Top Channels</Text>
                <Checkbox
                  type="checkbox"
                  checked={filterByTopChannels}
                  onChange={onFilterByTopChannelsChange}
                />
              </Flex>
            )}

            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Callsign:</Text>
              <StyledInput onChange={onCallsignChange} value={callsign} />
            </Flex>

            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Provider:</Text>
              <StyledInput onChange={onProviderChange} value={provider} />
            </Flex>

            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Flex mb={2} alignItems="center">
                <Text>Duration:</Text>
                <Box ml={2} data-tip="Enter duration in minutes. e.g; 120">
                  <Icon name="fas fa-info-circle"></Icon>
                </Box>
              </Flex>
              <Box>
                <Input
                  type="number"
                  style={{ width: "300px", textAlign: "right" }}
                  value={duration}
                  onChange={e => {
                    setDuration(Math.abs(e.target.value));
                  }}
                  min={0}
                />
              </Box>
            </Flex>
            <Divider />

            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Email list:</Text>
              <Box width="300px">
                <Creatable
                  isMulti
                  isClearable
                  value={emailList}
                  onChange={onEmailListChange}
                />
              </Box>
            </Flex>
            <Divider />

            <Flex alignItems="center" justifyContent="space-between" mb={2}>
              <Text>Active:</Text>
              <Toggle checked={active} onChange={e => onToggleChange(e)} />
            </Flex>
            <Box height={30}>
              <Text color="alert">{error}</Text>
            </Box>
          </Box>
        )}
      </Modal.Content>
      <Modal.Actions justifyContent="flex-end" borderColor="#d6d6d6 !important">
        <Flex>
          <Box mr={3}>
            <Button variant="secondary" onClick={onRequestClose}>
              Cancel
            </Button>
          </Box>
          <Button variant="primary" onClick={onAddNewClick} disabled={disabled}>
            {selectedAcr && selectedAcr.id ? "Update" : "Add New"}
          </Button>
        </Flex>
      </Modal.Actions>
      <StyledReactTooltip place="right" />
    </Modal>
  );
};

AddNewAcrQueryModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  addNewAcrQuery: PropTypes.func,
  updateAcrQuery: PropTypes.func,
  acrQueries: PropTypes.object,
  selectedAcr: PropTypes.object
};

const mapStateToProps = state => ({
  acrQueries: state.acrQueries
});

const mapDispatchToProps = dispatch => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddNewAcrQueryModal);
